/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/config/tenant.ts
 * PURPOSE: Tenant Configuration System - Multi-Tenant Branding Support
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 *
 * @business-context
 * - Domain: Multi-Tenant Platform Configuration
 * - Impact: Enables white-label deployments for multiple customers
 * - Stakeholders: Platform Administrators, Enterprise Customers
 *
 * @technical-architecture
 * - Layer: Configuration Layer
 * - Pattern: Environment-based configuration
 * - Dependencies: Vite environment variables
 */

export interface TenantConfig {
  /** Tenant display name (e.g., "Nhat Quang Holding", "ABC Restaurant Group") */
  name: string;

  /** Tenant tagline or subtitle */
  tagline: string;

  /** Logo URL or path */
  logo: string;

  /** Primary brand color (hex) */
  primaryColor?: string;

  /** Tenant identifier key */
  tenantKey?: string;
}

/**
 * Get tenant configuration from environment variables.
 *
 * Supports white-label deployments with configurable branding.
 *
 * Environment Variables:
 * - VITE_TENANT_NAME: Company name (default: "Multi-Industry BPM")
 * - VITE_TENANT_TAGLINE: Tagline (default: "Workforce Management Platform")
 * - VITE_TENANT_LOGO: Logo path (default: "/logo.svg")
 * - VITE_TENANT_PRIMARY_COLOR: Brand color (default: "#1890ff")
 * - VITE_TENANT_KEY: Tenant identifier (default: "platform")
 *
 * Example .env.production:
 * ```
 * VITE_TENANT_NAME="Nhat Quang Holding"
 * VITE_TENANT_TAGLINE="IPO Ready 2028"
 * VITE_TENANT_LOGO="/nqh-logo.svg"
 * VITE_TENANT_PRIMARY_COLOR="#8B4513"
 * VITE_TENANT_KEY="nqh-holdings"
 * ```
 *
 * @returns {TenantConfig} Tenant configuration object
 */
export const getTenantConfig = (): TenantConfig => {
  return {
    name: import.meta.env.VITE_TENANT_NAME || 'Multi-Industry BPM',
    tagline: import.meta.env.VITE_TENANT_TAGLINE || 'Workforce Management Platform',
    logo: import.meta.env.VITE_TENANT_LOGO || '/logo.svg',
    primaryColor: import.meta.env.VITE_TENANT_PRIMARY_COLOR || '#1890ff',
    tenantKey: import.meta.env.VITE_TENANT_KEY || 'platform',
  };
};

/**
 * Default tenant configuration (fallback).
 * Used when no environment variables are set.
 */
export const DEFAULT_TENANT_CONFIG: TenantConfig = {
  name: 'Multi-Industry BPM',
  tagline: 'Workforce Management Platform',
  logo: '/logo.svg',
  primaryColor: '#1890ff',
  tenantKey: 'platform',
};

/**
 * Get tenant name for display in UI.
 *
 * @returns {string} Tenant display name
 */
export const getTenantName = (): string => {
  return getTenantConfig().name;
};

/**
 * Get tenant tagline for display in footers/headers.
 *
 * @returns {string} Tenant tagline
 */
export const getTenantTagline = (): string => {
  return getTenantConfig().tagline;
};

/**
 * Get full tenant display text (name | tagline).
 *
 * Example: "Nhat Quang Holding | IPO Ready 2028"
 *
 * @returns {string} Formatted tenant display text
 */
export const getTenantDisplayText = (): string => {
  const config = getTenantConfig();
  return `${config.name} | ${config.tagline}`;
};
